import React, { useEffect, useReducer, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocaleContext } from '../../../../../context/LocaleContext';
import { useScheduleContext } from '../../../../../context/ScheduleContext';
import { unixFormat } from '../../../../../helpers/times';
import { hexToRgba } from '../../../../../helpers/hexToRgb';
import ScheduleEpisodeStatus from '../../../ScheduleComponents/ScheduleEpisodeStatus';
import ScheduleHeaderActions from '../../../ScheduleComponents/ScheduleHeaderActions';
import MembersSimpleView from '../../../../Members/MembersComponents/MembersSimpleView';
import PartnersBaseView from '../../../../Partners/PartnersViews/PartnersBaseView';
import { CTags } from '../../../../UI';
import EpisodeRecordStatus from '../../../ScheduleComponents/EpisodeRecordStatus';
import { useApplicationContext } from '../../../../../context/ApplicationContext';
import { useEventContext } from '../../../../../context/EventContext';
import storage from '../../../../../../../player/js/storage';
import Icons from '../../../../Icons';

import './style.scss';

const ScheduleGridCard = ({
    episode,
    settings = null,
    cardStyles = {},
    showHall = true,
    onEpisodeClick = null,
    onMemberSelect = null,
    currentUser,
    checkAuth,
    onRateEpisode,
}) => {
    const $locale = useLocaleContext();
    const {isWidget, page} = useScheduleContext();
    const { handleBookmark, bookmarkEpisodeIds } = useApplicationContext();
    const { event, eventKey } = useEventContext();
    const cardContainerRef = useRef(null);
    const childrenContentRef = useRef(null);
    const cardTitleRef = useRef(null);
    const ratedStorageKey = `${eventKey}/episodes/rated`;
    const ratedEpisodes = storage.get(ratedStorageKey) || {};
    const [isRated, setIsRated] = useState(ratedEpisodes[episode.id]?.includes(currentUser?.id));

    const [titleMeta, updateTitleMeta] = useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        {
            width: '100%',
            height: 'max-content',
            overflow: 'visible',
            display: '-webkit-box',
            WebkitLineClamp: 'initial',
            WebkitBoxOrient: 'vertical',
        },
    );

    const cardBackground = episode.paintInStreamColor && !!episode.stream.color
        ? hexToRgba(episode.stream.color, 10)
        : '';

    let delayBackground = !!episode.color
        ? hexToRgba(episode.color, episode.delayOpacity ?? 80)
        : `rgba(var(--app-color-light-rgb),${(episode.delayOpacity && episode.delayOpacity >= 0) ? episode.delayOpacity / 100 : 0.8})`;

    if (isWidget) {
        delayBackground = `rgba(var(--app-delayBackgroundColor),${(episode.delayOpacity && episode.delayOpacity >= 0) ? episode.delayOpacity / 100 : 0.8})`
    }

    useEffect(() => {
        hideContent();
    }, [cardStyles]);

    function hideContent() {
        if (isMobile || !cardContainerRef.current) return;

        const cardContainerRect = cardContainerRef.current.getBoundingClientRect();

        /** 18 - высота блока с названием зала, 2 - высота рамки */
        const cardHeight = cardContainerRect.height - (episode.hideStreamDescription ? 18 : 0) - (episode.hideStreamColor ? 2 : 0);
        let childrenContentHeight = (childrenContentRef.current.offsetHeight ?? 0);

        /** Получаем список элементов контента */
        const cardContentNodeList = document.querySelectorAll(`.schedule-card-grid-content-item-${episode.id}`);
        const cardContentChildren = Array.from(cardContentNodeList).reverse();

        cardContentChildren.forEach((child, index) => {
            if (cardHeight >= childrenContentHeight) {
                cardContentNodeList[cardContentChildren.length - 1 - index].style.opacity = 1;

                return;
            }

            childrenContentHeight -= child.offsetHeight;

            /** Если индекс равен индексу заголовка */
            if ((index === cardContentChildren.length - 2)) {
                if (!cardTitleRef.current) return;

                /** Вычисляем высоту одной строки заголовка и количество строк */
                const titleLineHeight = parseInt(getComputedStyle(cardTitleRef.current)?.lineHeight || (episode.displayNameLarge ? 24 : 16) * 1.3);
                const titleHeight = parseInt(getComputedStyle(cardTitleRef.current)?.height);

                const isContentPlaced = cardHeight > titleHeight;

                /** Прячем блок со статусами */
                cardContentNodeList[0].style.display = isContentPlaced ? 'flex' : 'none';

                const lineClamp = isContentPlaced ? 'initial' : Math.floor((cardHeight) / titleLineHeight);

                updateTitleMeta({
                    width: cardStyles.width,
                    height: isContentPlaced ? 'max-content' : parseInt(titleLineHeight) * lineClamp,
                    WebkitLineClamp: lineClamp,
                    overflow: 'hidden',
                });
            } else {
                cardContentNodeList[cardContentChildren.length - 1 - index].style.opacity = 0;
            }
        });
    }

    const handleRateToggle = async (e) => {
        e.stopPropagation();

        const isAuth = await checkAuth(currentUser);
        if (isAuth) {
            const value = !isRated;
            setIsRated(isRated => !isRated);
            const ratedEpisodes = storage.get(ratedStorageKey) || {};
            const arr = ratedEpisodes[episode.id] || [];
            const ratedStorageValue = { ...ratedEpisodes, [episode.id]: value ? [...arr, currentUser?.id] : arr.filter(id => id !== currentUser?.id) };
            onRateEpisode(ratedStorageKey, episode.id, value, ratedStorageValue);
        }
    }

    return (
        <div
            ref={cardContainerRef}
            data-episode={episode.id}
            className={`schedule-card-grid background-blur ${episode.importantEpisode ? 'schedule-card-grid--important' : '' } ${episode.delay ? 'schedule-card-grid--delay' : ''}`}
            style={{
                ...cardStyles,
                borderTop: (!!episode.stream?.color && !episode.hideStreamColor && !episode.delay) ? `2px solid ${episode.stream.color}` : 'none',
                backgroundColor: episode.delay ? delayBackground : cardBackground,
                backdropFilter: (episode.delay && !!episode.delayBlur) ? `blur(${episode.delayBlur}px)` : '',
            }}
            onClick={() => !episode.delay ? (onEpisodeClick?.(episode) ?? null) : null}
        >
            <div>
                {
                    (!episode.delay && !episode.hideStreamDescription && showHall) &&
                    <div className="schedule-card-grid__hall">
                        <span>
                            {$locale.getProperty(episode.stream, 'description')}
                        </span>
                    </div>
                }
                <div ref={childrenContentRef} className="schedule-card-grid-content">
                    <div className={`schedule-card-grid-header schedule-card-grid-content-item-${episode.id}`}>
                        <div className="schedule-card-grid-header__info">
                            <div className={`schedule-card-grid-header__time ${((episode.importantEpisode || episode.delay) && episode.displayTimeLarge) ? 'schedule-card-grid-header__time--lg' : ''}`}>
                                {unixFormat(episode.timeStart, 'HH:mm')} — {unixFormat(episode.timeStop, 'HH:mm')}
                            </div>
                            {
                                !episode.delay &&
                                <div className="schedule-card-grid-header-block">
                                    <EpisodeRecordStatus
                                        episode={episode}
                                        displayRecord={settings?.displayEpisodeRecord ?? false}
                                    >
                                        <ScheduleEpisodeStatus
                                            timeStart={episode.timeStart}
                                            timeStop={episode.timeStop}
                                            isVisible={settings?.showGridStatus ?? settings?.showStatuses}
                                        />
                                        <ScheduleHeaderActions
                                            episode={episode}
                                            isFormatVisible={settings?.showGridParticipationType ?? (settings?.showFormats ?? true)}
                                            isCalendarVisible={
                                                typeof settings?.showGridAddToCalendar === 'boolean' ? settings?.showGridAddToCalendar : settings?.showAddToCalendar ?? true
                                            }
                                            handleBookmark={handleBookmark}
                                            bookmarkEpisodeIds={bookmarkEpisodeIds}
                                            isBookmarkVisible={!isWidget && event?.moduleEpisode?.episodeFavorite}
                                        />
                                    </EpisodeRecordStatus>
                                </div>
                            }
                        </div>
                    </div>
                    <p
                        ref={cardTitleRef}
                        className={`schedule-card-grid-content-item-${episode.id} ${!episode.delay && !episode.importantEpisode
                            ? 'schedule-card-grid__title'
                            : `schedule-card-grid__title ${episode.displayNameLarge ? 'schedule-card-grid__title--lg' : ''}`}`}
                        style={{
                            color: (!isWidget && episode.delay) ? 'rgb(var(--app-color-dark-rgb))' : '',
                            ...titleMeta,
                        }}
                    >
                        {$locale.getProperty(episode, 'name')}
                    </p>
                    {
                        !!episode.tags?.length &&
                        <div className={`schedule-card-grid-content-item-${episode.id}`}>
                            <CTags tags={episode.tags} />
                        </div>
                    }
                    {
                        !!episode.partners?.length &&
                        <div className={`schedule-card-grid__partners schedule-card-grid-content-item-${episode.id}`}>
                            <PartnersBaseView
                                partners={episode.partners}
                                size="sm"
                            />
                        </div>
                    }
                    {
                        !!episode.groups?.length &&
                        <div className={`schedule-card-grid__members schedule-card-grid-content-item-${episode.id}`}>
                            <MembersSimpleView
                                episode={episode}
                                hasPhotos={
                                    settings?.showGridMembersPhoto && page?.settings?.showPhotos
                                }
                                hasNames={
                                    settings?.showGridMembersName ?? true
                                }
                                onMemberClick={(member) => onMemberSelect?.(member) ?? null}
                            />
                        </div>
                    }
                </div>
            </div>
            <div className="schedule-card-grid-footer">
                {onRateEpisode && !episode.delay && (
                    <a onClick={handleRateToggle} className={`schedule-card-grid-like ${isRated ? '-active' : ''}`}>
                        <Icons name="like_message" active={isRated} />{episode.like}
                    </a>
                )}
            </div>
        </div>
    )
};

export default ScheduleGridCard;
